import I18n from 'I18n';
import { Paths } from './RoutesPublic';
import styled, { css } from 'styled-components';
import enviro from 'enviro';
import { CANDY_APPLE_DARK, GREAT_WHITE, MARIGOLD_DARK, MARIGOLD_LIGHT, MARIGOLD_MEDIUM, OLAF } from 'HubStyleTokens/colors';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import { mediaQueries, MOBILE_MENU_BREAKPOINT } from 'developer-docs-shared-components/utils/mediaQueries';
import ResponsiveRenderer from 'developer-docs-shared-components/components/ResponsiveRenderer';
import Search from './header/Search';
import { useEffect, useMemo } from 'react';
import HamburgerMenu from './header/HamburgerMenu';
import DrilldownMenu from './DrilldownMenu';
import { useViewport } from 'developer-docs-shared-components/hooks/useViewport';
import { useLocation } from 'react-router-dom';
import { useFullscreen } from '../hooks/useFullscreen';
import { StyledFooter } from '../utils/css';
import HubSpotDevLogo from './HubSpotDevLogo';
import { SECONDARY_NAV_WIDTH } from '../utils/css';
import PrimaryNav from './PrimaryNav';
import PrimaryNavSubmenu from './PrimaryNavSubmenu';
import { getPrimaryNavItems } from '../config/nav/primaryNavConfig';
import LanguageSwitcher from './header/LanguageSwitcher';
import { AccountLink } from './AccountLink';

/**
 * The left padding, in pixels, at desktop. Used to align search input with main
 * contents on API Reference pages.
 * @internal
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const HEADER_PADDING_LEFT_DESKTOP = 40;
/**
 * The `<header />` for the public-facing docs site.
 */
const Header = styled.header.withConfig({
  displayName: "Header",
  componentId: "q5vmgb-0"
})(["background-color:", ";border-bottom:2px solid ", ";margin:0 auto;width:100%;", " ", "{padding:16px;min-height:", ";", "{border-top:1px solid ", ";}}"], OLAF, GREAT_WHITE, () => {
  if (enviro.isQa() && enviro.deployed()) {
    // Show banner on QA
    return css(["position:relative;&::before{content:'QA';opacity:0.15;position:absolute;pointer-events:none;color:", ";letter-spacing:2px;line-height:2;font-size:2em;text-align:center;z-index:1;top:0;left:0;width:100%;height:100%;background:repeating-linear-gradient( 45deg,transparent,transparent 10px,", " 10px,", " 20px ),linear-gradient(to bottom,", ",", ");}"], CANDY_APPLE_DARK, MARIGOLD_MEDIUM, MARIGOLD_MEDIUM, MARIGOLD_LIGHT, MARIGOLD_DARK);
  }
  return null;
}, mediaQueries.belowWidth(MOBILE_MENU_BREAKPOINT), ({
  open
}) => open ? '100vh' : null, StyledFooter, GREAT_WHITE);
const InnerWrapper = styled.div.withConfig({
  displayName: "Header__InnerWrapper",
  componentId: "q5vmgb-1"
})(["position:relative;padding:0 20px 0 ", "px;", "{padding:16px 4px 16px 0;}"], HEADER_PADDING_LEFT_DESKTOP, mediaQueries.belowWidth(MOBILE_MENU_BREAKPOINT));

/**
 * A router-aware link wrapper for the HubSpot logo.
 */
const LogoLink = styled(UIRouterLink).withConfig({
  displayName: "Header__LogoLink",
  componentId: "q5vmgb-2"
})(["display:inline-block;white-space:nowrap;"]);

/**
 * At mobile, wraps the drilldown menu.
 */
const MobileDrilldownMenuWrapper = styled.div.withConfig({
  displayName: "Header__MobileDrilldownMenuWrapper",
  componentId: "q5vmgb-3"
})(["flex:1 1 auto;margin:12px 0;"]);
const searchSubHeaderStyles = css(["background-color:unset;& ", "{transition:visibility 0s linear 0s,opacity 0.3s;opacity:0;visibility:hidden;}"], MobileDrilldownMenuWrapper);
const searchNavWrapperDefaultStyles = css(["background-color:", ";transition:visibility 0s linear 0s,opacity 0.3s;opacity:", ";visibility:", ";z-index:6;"], OLAF, ({
  open
}) => open ? 1 : 0, ({
  open
}) => open ? 'visible' : 'hidden');

/**
 * At desktop, provides the same width as the secondary nav sidebar.
 */
const DesktopLogoWrapper = styled.div.withConfig({
  displayName: "Header__DesktopLogoWrapper",
  componentId: "q5vmgb-4"
})(["display:inline-block;position:relative;top:4px;width:222px;vertical-align:middle;", "{width:242px;}"], mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT));

/**
 * Wraps the search and nav. At mobile, these contents are displayed when the
 * nav is open.
 */
const SearchNavWrapper = styled.div.withConfig({
  displayName: "Header__SearchNavWrapper",
  componentId: "q5vmgb-5"
})(["display:inline-flex;justify-content:space-between;align-items:center;width:calc(100% - ", "px);", "{display:flex;flex-direction:column;position:absolute;top:calc(100% + 16px);left:0;height:calc(100vh - 116px);width:100%;overflow:hidden;align-items:normal;", "}"], SECONDARY_NAV_WIDTH - HEADER_PADDING_LEFT_DESKTOP, mediaQueries.belowWidth(MOBILE_MENU_BREAKPOINT), ({
  showSubHeaderSearch
}) => showSubHeaderSearch ? searchSubHeaderStyles : searchNavWrapperDefaultStyles);
const StyledDiv = styled.div.withConfig({
  displayName: "Header__StyledDiv",
  componentId: "q5vmgb-6"
})(["display:flex;align-items:center;"]);

/**
 * On the SRP at tablet viewports (i.e. between mobile and desktop) this input
 * should not be rendered to the DOM since the main SRP container will house
 * the input instead.
 */
const NullOnSRPTablet = ({
  isSearchResultsPage,
  children
}) => {
  const {
    isBelowLaptop,
    isMobile
  } = useViewport();
  if (isSearchResultsPage && !isMobile && isBelowLaptop) {
    return null;
  }
  return children;
};
const Logo = styled(HubSpotDevLogo).withConfig({
  displayName: "Header__Logo",
  componentId: "q5vmgb-7"
})(["", "{max-width:45vw;}"], mediaQueries.belowWidth(MOBILE_MENU_BREAKPOINT));
const DocsHeader = ({
  isSearchResultsPage
}) => {
  const location = useLocation();
  const [fullscreen, setFullscreen] = useFullscreen();
  const {
    isMobile
  } = useViewport();
  const primaryNavConfig = getPrimaryNavItems();
  const showSubHeaderSearch = useMemo(() => location.pathname === '/search' && isMobile && !fullscreen, [location, isMobile, fullscreen]);
  useEffect(() => {
    setFullscreen(false);
  }, [location, setFullscreen]);
  const logoLinkNode = /*#__PURE__*/_jsx(LogoLink, {
    to: Paths.HOME,
    "aria-label": I18n.text(`developer-docs-ui.htmlTitle`),
    children: /*#__PURE__*/_jsx(Logo, {
      title: I18n.text(`developer-docs-ui.accessibility.logoAltText`)
    })
  });
  return /*#__PURE__*/_jsxs(Header, {
    "data-test-id": "docs-public-header",
    open: fullscreen,
    children: [/*#__PURE__*/_jsxs(InnerWrapper, {
      children: [/*#__PURE__*/_jsx(ResponsiveRenderer, {
        renderDesktop: () => /*#__PURE__*/_jsx(DesktopLogoWrapper, {
          children: logoLinkNode
        }),
        renderMobile: () => logoLinkNode
      }), /*#__PURE__*/_jsx(HamburgerMenu, {
        open: fullscreen,
        onClick: () => setFullscreen(!fullscreen)
      }), /*#__PURE__*/_jsx(SearchNavWrapper, {
        "data-open": fullscreen,
        open: fullscreen,
        showSubHeaderSearch: showSubHeaderSearch,
        children: /*#__PURE__*/_jsx(ResponsiveRenderer, {
          renderDesktop: () => /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(PrimaryNav, {
              primaryNavConfig: primaryNavConfig
            }), /*#__PURE__*/_jsxs(StyledDiv, {
              children: [/*#__PURE__*/_jsx(NullOnSRPTablet, {
                isSearchResultsPage: isSearchResultsPage,
                children: /*#__PURE__*/_jsx(Search, {})
              }), /*#__PURE__*/_jsx(LanguageSwitcher, {}), /*#__PURE__*/_jsx(AccountLink, {})]
            })]
          }),
          renderMobile: () => /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(Search, {}), /*#__PURE__*/_jsx(MobileDrilldownMenuWrapper, {
              "data-test-id": "mobile-drilldown-menu",
              children: /*#__PURE__*/_jsx(DrilldownMenu, {})
            }), /*#__PURE__*/_jsx(AccountLink, {
              isMobile: true
            })]
          })
        })
      })]
    }), /*#__PURE__*/_jsx(PrimaryNavSubmenu, {
      primaryNavConfig: primaryNavConfig
    })]
  });
};
export default DocsHeader;